import keywordTrackerApi from '@/app/KeywordTracker/api/keywordTrackerApi';
import type {
  DomainSlugArg,
  GenericBody,
  CreateKeywordsResponse,
  GetDomainSettingsResponse,
  TrackerTariffResponse,
  UpdateDomainSettingsRequest,
  UpdateDomainSettingsResponse
} from '@/app/KeywordTracker/types/keywordTrackerApiTypes';
import type {
  CompetitorDomainMetricsRequest,
  CompetitorDomainMetricsResponse,
  CompetitorsForSelectResponse
} from '@/app/KeywordTracker/types/competitorDomainMetricsTypes';

const keywordsApi = keywordTrackerApi.injectEndpoints({
  endpoints: build => ({
    createAiAnalysis: build.mutation<unknown, GenericBody>({
      query: body => ({
        url: '/ai-analytics/v2/keyword-tracker-analysis/',
        method: 'POST',
        body
      })
    }),
    getKeywordNotes: build.query<unknown, GenericBody>({
      query: body => ({
        url: '/keyword_tracker/v2/keyword_notes/get',
        method: 'POST',
        body
      }),
      providesTags: ['keywordsNote']
    }),
    createKeywordNote: build.mutation<unknown, GenericBody>({
      query: body => ({
        url: '/keyword_tracker/v2/keyword_notes/add',
        method: 'POST',
        body
      }),
      invalidatesTags: ['keywordsNote']
    }),
    deleteKeywordNote: build.mutation<unknown, GenericBody>({
      query: body => ({
        url: '/keyword_tracker/v2/keyword_notes/delete',
        method: 'POST',
        body
      }),
      invalidatesTags: ['keywordsNote']
    }),
    updateKeywordNote: build.mutation<unknown, GenericBody>({
      query: body => ({
        url: '/keyword_tracker/v2/keyword_notes/update',
        method: 'POST',
        body
      }),
      invalidatesTags: ['keywordsNote']
    }),
    getKeywordsCharts: build.query<unknown, GenericBody>({
      query: body => ({
        url: '/keyword_tracker/v2/keywords/charts',
        method: 'POST',
        body
      }),
      providesTags: ['keywordsPage']
    }),
    getAvgRanksChart: build.query<unknown, GenericBody>({
      query: body => ({
        url: '/keyword_tracker/v2/keywords/avg_ranks_chart',
        method: 'POST',
        body
      })
    }),
    createKeywords: build.mutation<CreateKeywordsResponse, GenericBody>({
      query: body => ({
        url: '/keyword_tracker/v2/keywords/create',
        method: 'POST',
        body
      }),
      invalidatesTags: ['domains_for_updating']
    }),
    deleteKeywords: build.mutation<unknown, GenericBody>({
      query: body => ({
        url: '/keyword_tracker/v2/keywords/delete',
        method: 'POST',
        body
      }),
      invalidatesTags: []
    }),
    getKeywords: build.query<unknown, GenericBody>({
      query: body => ({
        url: '/keyword_tracker/v2/keywords/get',
        method: 'POST',
        body
      }),
      providesTags: ['keywordsPage']
    }),
    getKeywordsFunc: build.mutation<unknown, GenericBody>({
      query: body => ({
        url: '/keyword_tracker/v2/keywords/get',
        method: 'POST',
        body
      }),
      providesTags: ['keywordsPage']
    }),
    getKeywordsForSelect: build.query<unknown, DomainSlugArg>({
      query: domain_slug =>
        '/keyword_tracker/v2/keywords/for_select?domainSlug=' + domain_slug,
      providesTags: ['keywordsForSelect']
    }),
    getKeywordsProgressChart: build.query<unknown, GenericBody>({
      query: body => ({
        url: '/keyword_tracker/v2/keywords/rank_progress_chart',
        method: 'POST',
        body
      }),
      providesTags: ['KeywordsProgressChart']
    }),
    getRankHistory: build.query<unknown, GenericBody>({
      query: body => ({
        url: '/keyword_tracker/v2/keywords/rank_history',
        method: 'POST',
        body
      })
    }),
    getRankHistoryFunc: build.mutation<unknown, GenericBody>({
      query: body => ({
        url: '/keyword_tracker/v2/keywords/rank_history',
        method: 'POST',
        body
      })
    }),
    getRelatedKeywords: build.query<unknown, GenericBody>({
      query: body => ({
        url: '/keyword_tracker/v2/keywords/related',
        method: 'POST',
        body
      })
    }),
    getGoogleResultHistory: build.query<unknown, GenericBody>({
      query: body => ({
        url: '/keyword_tracker/v2/keywords/google_result_history2',
        method: 'POST',
        body
      })
    }),
    getSettings: build.query<GetDomainSettingsResponse, DomainSlugArg>({
      query: domain_slug => ({
        url:
          '/keyword_tracker/v2/domains/get_settings?domainSlug=' + domain_slug,
        method: 'POST'
      })
    }),
    setOldSettings: build.mutation<
      UpdateDomainSettingsResponse,
      UpdateDomainSettingsRequest
    >({
      query: body => ({
        url: '/keyword_tracker/v2/domains/update_settings',
        method: 'POST',
        body
      }),
      invalidatesTags: ['tracker_info']
    }),
    setSettings: build.mutation<
      UpdateDomainSettingsResponse,
      UpdateDomainSettingsRequest
    >({
      query: body => ({
        url: '/keyword_tracker/v2/domains/update_settings2',
        method: 'POST',
        body
      }),
      invalidatesTags: ['tracker_info']
    }),
    getDomainsForUpdating: build.query<unknown, void>({
      query: () => ({
        url: '/keyword_tracker/v2/domains/domains_for_updating',
        method: 'POST'
      }),
      providesTags: ['domains_for_updating']
    }),
    updateRanks: build.mutation<unknown, GenericBody>({
      query: body => ({
        url: '/keyword_tracker/v2/keywords/update_ranks',
        method: 'POST',
        body
      }),
      invalidatesTags: ['domains_for_updating']
    }),
    getKeywordSearchVolume: build.query<unknown, GenericBody>({
      query: body => ({
        url: '/keyword_tracker/v2/keywords/get_search_volume',
        method: 'POST',
        body
      })
    }),
    getKeywordsMap: build.query<unknown, GenericBody>({
      query: body => ({
        url: '/keyword_tracker/v2/keywords/map',
        method: 'POST',
        body
      }),
      providesTags: ['KeywordsMap']
    }),
    getTags: build.query<unknown, GenericBody>({
      query: body => ({
        url: '/keyword_tracker/v2/tags/count',
        method: 'POST',
        body
      }),
      providesTags: ['tags']
    }),
    getTagsFunc: build.mutation<unknown, GenericBody>({
      query: body => ({
        url: '/keyword_tracker/v2/tags/get',
        method: 'POST',
        body
      }),
      providesTags: ['tags']
    }),
    getTagsForSelect: build.query<unknown, DomainSlugArg>({
      query: domain_slug => ({
        url: '/keyword_tracker/v2/tags/for_select?domainSlug=' + domain_slug,
        method: 'GET'
      }),
      providesTags: ['tagsForSelect']
    }),
    createTag: build.mutation<unknown, GenericBody>({
      query: body => ({
        url: '/keyword_tracker/v2/tags/create',
        method: 'POST',
        body
      }),
      invalidatesTags: ['tracker_info', 'tagsForSelect']
    }),
    deleteTags: build.mutation<unknown, GenericBody>({
      query: body => ({
        url: '/keyword_tracker/v2/tags/delete',
        method: 'POST',
        body
      }),
      invalidatesTags: ['tracker_info', 'keywordsPage', 'tagsForSelect']
    }),
    editTag: build.mutation<unknown, GenericBody>({
      query: body => ({
        url: '/keyword_tracker/v2/tags/edit',
        method: 'POST',
        body
      }),
      invalidatesTags: ['keywordsPage', 'tagsForSelect']
    }),
    moveToTag: build.mutation<unknown, GenericBody>({
      query: body => ({
        url: '/keyword_tracker/v2/tags/move_to_tags',
        method: 'POST',
        body
      })
    }),
    leaveToTags: build.mutation<unknown, GenericBody>({
      query: body => ({
        url: '/keyword_tracker/v2/tags/leave_the_tags',
        method: 'POST',
        body
      })
    }),
    moveToGroup: build.mutation<unknown, GenericBody>({
      query: body => ({
        url: '/keyword_tracker/v2/groups/move_to_group',
        method: 'POST',
        body
      })
    }),
    leaveToGroup: build.mutation<unknown, GenericBody>({
      query: body => ({
        url: '/keyword_tracker/v2/groups/leave_to_group',
        method: 'POST',
        body
      })
    }),
    moveToLanding: build.mutation<unknown, GenericBody>({
      query: body => ({
        url: '/keyword_tracker/v2/landing_urls/move_to_landing',
        method: 'POST',
        body
      })
    }),
    leaveToLanding: build.mutation<unknown, GenericBody>({
      query: body => ({
        url: '/keyword_tracker/v2/landing_urls/leave_to_landing',
        method: 'POST',
        body
      })
    }),
    editLanding: build.mutation<unknown, GenericBody>({
      query: body => ({
        url: '/keyword_tracker/v2/landing_urls/edit',
        method: 'POST',
        body
      }),
      invalidatesTags: ['keywordsPage']
    }),
    getLandingUrlsForSelect: build.query<unknown, DomainSlugArg>({
      query: domain_slug =>
        '/keyword_tracker/v2/landing_urls/for_select?domainSlug=' + domain_slug,
      providesTags: ['landingUrlsForSelect']
    }),
    getLandingUrls: build.query<unknown, GenericBody>({
      query: body => ({
        url: '/keyword_tracker/v2/landing_urls/count',
        method: 'POST',
        body
      }),
      providesTags: ['landing_urls']
    }),
    getLandingUrlsFunc: build.mutation<unknown, GenericBody>({
      query: body => ({
        url: '/keyword_tracker/v2/landing_urls/get',
        method: 'POST',
        body
      }),
      providesTags: ['landing_urls']
    }),
    createLandingUrls: build.mutation<unknown, GenericBody>({
      query: body => ({
        url: '/keyword_tracker/v2/landing_urls/create',
        method: 'POST',
        body
      }),
      invalidatesTags: ['tracker_info', 'landingUrlsForSelect']
    }),
    deleteLandingUrls: build.mutation<unknown, GenericBody>({
      query: body => ({
        url: '/keyword_tracker/v2/landing_urls/delete',
        method: 'POST',
        body
      }),
      invalidatesTags: ['tracker_info', 'keywordsPage', 'landingUrlsForSelect']
    }),
    getGroups: build.query<unknown, GenericBody>({
      query: body => ({
        url: '/keyword_tracker/v2/groups/count',
        method: 'POST',
        body
      }),
      providesTags: ['groups']
    }),
    getGroupsFunc: build.mutation<unknown, GenericBody>({
      query: body => ({
        url: '/keyword_tracker/v2/groups/get',
        method: 'POST',
        body
      }),
      providesTags: ['groups']
    }),
    getLanguagesForSelect: build.query<unknown, void>({
      query: () => '/keyword_tracker/v2/languages/for_select'
    }),
    getLocationsForSelect: build.query<unknown, void>({
      query: () => '/keyword_tracker/v2/locations/for_select'
    }),
    createGroup: build.mutation<unknown, GenericBody>({
      query: body => ({
        url: '/keyword_tracker/v2/groups/create',
        method: 'POST',
        body
      }),
      invalidatesTags: ['tracker_info', 'groupsForSelect']
    }),
    editGroup: build.mutation<unknown, GenericBody>({
      query: body => ({
        url: '/keyword_tracker/v2/groups/edit',
        method: 'POST',
        body
      }),
      invalidatesTags: ['keywordsPage', 'groupsForSelect']
    }),
    deleteGroups: build.mutation<unknown, GenericBody>({
      query: body => ({
        url: '/keyword_tracker/v2/groups/delete',
        method: 'POST',
        body
      }),
      invalidatesTags: ['tracker_info', 'keywordsPage', 'groupsForSelect']
    }),
    getCompetitors: build.query<unknown, GenericBody>({
      query: body => ({
        url: '/keyword_tracker/v2/competitors/count',
        method: 'POST',
        body
      }),
      providesTags: ['competitors', 'tracker_info']
    }),
    getCompetitorsFunc: build.mutation<unknown, GenericBody>({
      query: body => ({
        url: '/keyword_tracker/v2/competitors/get',
        method: 'POST',
        body
      }),
      providesTags: ['competitors']
    }),
    createCompetitor: build.mutation<unknown, GenericBody>({
      query: body => ({
        url: '/keyword_tracker/v2/competitors/create',
        method: 'POST',
        body
      }),
      invalidatesTags: [
        'competitors',
        'tracker_info',
        'keywordsPage',
        'competitor_domain_metrics'
      ]
    }),
    deleteCompetitor: build.mutation<unknown, GenericBody>({
      query: body => ({
        url: '/keyword_tracker/v2/competitors/delete',
        method: 'POST',
        body
      }),
      invalidatesTags: [
        'competitors',
        'tracker_info',
        'keywordsPage',
        'competitor_chart',
        'competitor_domain_metrics'
      ]
    }),
    getCompetitorsForSelect: build.query<
      CompetitorsForSelectResponse,
      DomainSlugArg
    >({
      query: domain_slug =>
        `/keyword_tracker/v2/competitors/for_select?domainSlug=${domain_slug}`,
      providesTags: ['competitors']
    }),
    getCompetitorDomainMetrics: build.query<
      CompetitorDomainMetricsResponse,
      CompetitorDomainMetricsRequest
    >({
      query: body => ({
        url: '/keyword_tracker/v2/competitors/get_domain_metrics',
        method: 'POST',
        body
      }),
      providesTags: ['competitor_domain_metrics']
    }),
    getCompetitorsChart: build.query<unknown, GenericBody>({
      query: body => ({
        url: '/keyword_tracker/v2/competitors/get_ranks_chart',
        method: 'POST',
        body
      }),
      providesTags: ['competitor_chart']
    }),
    getCompetitorsAvgRankChart: build.query<unknown, GenericBody>({
      query: body => ({
        url: '/keyword_tracker/v2/competitors/get_avg_ranks_chart_v2',
        method: 'POST',
        body
      }),
      providesTags: ['competitor_chart']
    }),
    getTariffInfo: build.query<TrackerTariffResponse, DomainSlugArg>({
      query: domain_slug => ({
        url: '/go_handler/v2/tariff/get2',
        method: 'POST',
        body: { domain_slug }
      }),
      providesTags: ['tracker_info']
    })
  })
});

export const {
  useGetTariffInfoQuery,
  useGetKeywordsQuery,
  useGetKeywordsForSelectQuery,
  useGetKeywordsFuncMutation,
  useGetKeywordsChartsQuery,
  useGetKeywordsProgressChartQuery,
  useLazyGetKeywordsProgressChartQuery,
  useGetRankHistoryFuncMutation,
  useGetRankHistoryQuery,
  useGetRelatedKeywordsQuery,
  useGetGoogleResultHistoryQuery,
  useLazyGetSettingsQuery,
  useSetSettingsMutation,
  useGetKeywordSearchVolumeQuery,
  useLazyGetKeywordSearchVolumeQuery,
  useGetKeywordsMapQuery,
  useGetTagsQuery,
  useGetTagsFuncMutation,
  useGetTagsForSelectQuery,
  useCreateTagMutation,
  useGetLandingUrlsQuery,
  useGetLandingUrlsFuncMutation,
  useGetLandingUrlsForSelectQuery,
  useEditLandingMutation,
  useGetLanguagesForSelectQuery,
  useGetLocationsForSelectQuery,
  useCreateKeywordsMutation,
  useDeleteKeywordsMutation,
  useMoveToTagMutation,
  useMoveToGroupMutation,
  useDeleteTagsMutation,
  useEditTagMutation,
  useCreateLandingUrlsMutation,
  useDeleteLandingUrlsMutation,
  useMoveToLandingMutation,
  useCreateGroupMutation,
  useEditGroupMutation,
  useDeleteGroupsMutation,
  useGetGroupsQuery,
  useGetGroupsFuncMutation,
  useGetCompetitorsFuncMutation,
  useGetCompetitorsQuery,
  useCreateCompetitorMutation,
  useDeleteCompetitorMutation,
  useGetCompetitorsForSelectQuery,
  useGetCompetitorDomainMetricsQuery,
  useGetCompetitorsChartQuery,
  useGetCompetitorsAvgRankChartQuery,
  useGetDomainsForUpdatingQuery,
  useUpdateRanksMutation,
  useLeaveToGroupMutation,
  useLeaveToLandingMutation,
  useLeaveToTagsMutation,
  useGetAvgRanksChartQuery,
  useLazyGetCompetitorsAvgRankChartQuery,
  useGetKeywordNotesQuery,
  useCreateAiAnalysisMutation,
  useCreateKeywordNoteMutation,
  useDeleteKeywordNoteMutation,
  useUpdateKeywordNoteMutation,
  useSetOldSettingsMutation
} = keywordsApi;

export default keywordsApi;
